import type { InventoryResource, Condition } from '@repo/api-gw-sdk';
import type {
  Column,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';

import { Table } from '@/components/table';
import { useColumns } from '@/components/table/hooks';
import { useDAL } from '@/contexts/useDAL';

import { InventoryMultiInstanceMenu } from './InventoryMultiInstanceMenu';
import {
  fullInventoryListTableColumns,
  inventoryListTableColumns,
  inventoryListTableHiddenColumns,
} from './inventoryListTableColumns';

interface InventoryListTableProps {
  showFiltersBar: boolean;
  sorting: SortingState;
  pagination: PaginationState;
  query?: Condition;
  onSortingChange: (sortField: string, desc: boolean) => void;
  onPaginationChange: (value: PaginationState) => void;
  paginationBarActions?: React.ComponentType<{
    columns: Column<InventoryResource>[];
  }>[];
  isMultiSelect: boolean;
  initialSelectedId?: string;
  onRowSelectionChange?: (entities: string[]) => void;
  refreshChildTrigger?: number;
  triggerInventoryRefresh?: () => void;
}

export const InventoryListTable = (props: InventoryListTableProps) => {
  const dal = useDAL();

  const {
    data: body,
    isLoading,
    mutate: mutateAllInventory,
  } = dal.inventory.useList(props.pagination, props.sorting[0], props.query);

  useEffect(() => {
    if (props.refreshChildTrigger) {
      void mutateAllInventory();
    }
  }, [mutateAllInventory, props.refreshChildTrigger]);

  const columns = useColumns<InventoryResource>(
    props.isMultiSelect
      ? fullInventoryListTableColumns
      : inventoryListTableColumns
  );

  const MultiSelectActionButtonWithRefresh = useMemo(
    () => (buttonProps: { entities: InventoryResource[] }) => (
      <InventoryMultiInstanceMenu
        entities={buttonProps.entities}
        triggerInventoryRefresh={props.triggerInventoryRefresh}
      />
    ),
    [props.triggerInventoryRefresh]
  );

  return (
    <Table
      entityLabel='resources'
      showFiltersBar={props.showFiltersBar}
      data={body?.resources || []}
      testId='inventory-table'
      isMultiSelect={props.isMultiSelect}
      MultiSelectActionButton={MultiSelectActionButtonWithRefresh}
      columns={columns}
      hiddenColumns={inventoryListTableHiddenColumns.map((x) => x.name)}
      stickyHeader
      // pinnedColumns={[backupStatus.name, resourceName.name]}
      sorting={props.sorting}
      isLoading={isLoading}
      onSortingChanged={(state) =>
        props.onSortingChange(state[0].id, state[0].desc)
      }
      pagination={{
        ...props.pagination,
        recordsCount: body?.pagination.recordsCount,
        onChange: props.onPaginationChange,
      }}
      paginationBarActions={props.paginationBarActions}
      initialSelectedId={props.initialSelectedId}
      onRowSelectionChange={props.onRowSelectionChange}
    />
  );
};
